import React from 'react';
import {IController} from '@src/@interfaces/services/ControllersRepository/IController';
import {IClientRoute} from '@src/@interfaces/services/Router';
import {ComponentChildren} from '@src/@types/ComponentChildren';
import Error404 from '@views/errors/Error404';
import ListView from '@src/components/_views/configurable/ListView';
import {ActionComponent, ControllerActionComponents} from '@src/@types/controllers/AbstractController';
import FormView from '@src/components/_views/configurable/FormView';
import CreateView from '@src/components/_views/configurable/CreateView';
import Cache from '@src/classes/Cacheable/Cache';
import {ICache} from '@src/@interfaces/classes/Cacheable/ICache';
import ImportView from '@src/components/_views/configurable/import-view/ImportView';

export default abstract class AbstractController implements IController {
    #availableActionsMap: Record<string, number> | undefined;

    protected abstract controllerName: string;

    protected availableActions!: string[];
    protected actionsComponents!: ControllerActionComponents;
    protected fallbackComponent: React.ComponentType = Error404;

    protected cache!: ICache<Record<string, any>>;

    constructor() {
        this.cache = new Cache<any>();

        const defaultActionsWithComponents = this.getDefaultActionsWithComponents();
        this.availableActions ??= Object.keys(defaultActionsWithComponents);
        this.actionsComponents ??= defaultActionsWithComponents;

        this.initializeActions();
    }

    // eslint-disable-next-line
    protected initializeActions(): void {
    }

    getName(): string {
        return this.controllerName;
    }

    getActionsRoutes(): IClientRoute[] {
        return [];
    }

    getActionComponent(actionName: string): React.ComponentType<any> {
        let component;
        if (this.isAvailableAction(actionName) && actionName in this.actionsComponents) {
            component = this.actionsComponents[actionName];
        }

        if (!component) {
            return this.getFallbackComponent();
        } else if (Array.isArray(component)) {
            return component[0];
        } else {
            return component;
        }
    }

    getActionComponentProps(actionName: string): any {
        return this.cache.remember(`${actionName}-props`, () => {
            const defaultProps = {controllerName: this.getName()};
            const component = this.actionsComponents[actionName];

            if (
                    !this.isAvailableAction(actionName) ||
                    !Array.isArray(component)
            ) {
                return defaultProps;
            }

            return {...defaultProps, ...component[1]};
        });
    }

    getFallbackComponent(): React.ComponentType<any> {
        return this.fallbackComponent;
    }

    isAvailableAction(action: string): boolean {
        return action in this.#getAvailableActionsMap();
    }

    render(action: string): ComponentChildren {
        const Element = this.getActionComponent(action);

        return <Element controllerName={this.controllerName}/>;
    }

    protected getDefaultActionsWithComponents(): ControllerActionComponents {
        const defaults = {
            index: this.getListPageComponent(),
            store: this.getCreatePageComponent(),
            show: this.getShowPageComponent(),
            import: this.getImportPageComponent(),
        };

        const result: ControllerActionComponents = {};
        for (const [key, value] of Object.entries(defaults)) {
            if (value != null) {
                result[key] = value;
            }
        }

        return result;
    }

    protected getListPageComponent(): ControllerActionComponents[string] | null {
        return this.getDefaultListComponent();
    }

    protected getImportPageComponent(): ControllerActionComponents[string] | null {
        return this.getDefaultImportComponent();
    }

    protected getShowPageComponent(): ControllerActionComponents[string] | null {
        return this.getDefaultUpdateFormComponent();
    }

    protected getCreatePageComponent(): ControllerActionComponents[string] | null {
        return this.getDefaultCreateFormComponent();
    }

    protected getDefaultListComponent(): ControllerActionComponents[string] {
        return ListView;
    }

    protected getDefaultImportComponent(): ControllerActionComponents[string] {
        return ImportView;
    }

    protected getDefaultUpdateFormComponent(): ControllerActionComponents[string] {
        return FormView;
    }

    protected getDefaultCreateFormComponent(): ControllerActionComponents[string] {
        return CreateView;
    }

    protected appendActions(...actions: string[]): IController {
        this.availableActions = this.availableActions.concat(actions);
        this.#refreshAvailableActionsMap();

        return this;
    }

    protected appendActionComponent(action: string, component: ActionComponent, componentProps?: Record<string, any>): IController {
        this.actionsComponents[action] ??= componentProps !== undefined ? [component, componentProps] : component;

        return this;
    }

    #getAvailableActionsMap(): Record<string, number> {
        if (this.#availableActionsMap === undefined) {
            this.#refreshAvailableActionsMap();
        }

        return this.#availableActionsMap as Record<string, number>;
    }

    #refreshAvailableActionsMap(): void {
        this.#availableActionsMap = this.availableActions.reduce((res, action, i) => {
            res[action] = i;

            return res;
        }, {} as Record<string, number>);
    }
}
